// Synchronous facade for the PostHog analytics SDK. Eagerly imported from
// `src/analytics/index.ts`; intentionally contains NO dynamic imports so the
// rolldown chunk graph cannot form a cycle through Vite's __vitePreload
// runtime helper. The actual `posthog-js` SDK loads via `posthog-impl.ts`,
// which is dynamic-imported from the analytics bootstrap.
import type { PostHog } from 'posthog-js';
import { eventSchemas, type EventName, type EventProps } from './events';

interface BufferedCall {
  name: EventName;
  props: Record<string, unknown>;
}

let client: PostHog | null = null;
let buffer: BufferedCall[] = [];

// Stored at init time so deleteMyData() can reference them without
// calling client.config.token / client.get_distinct_id() — those may
// not be available (e.g. in test mocks or before the SDK is fully ready).
let _apiKey: string | null = null;
let _distinctId: string | null = null;
const _apiHost = 'https://us.i.posthog.com';

/**
 * Called by `posthog-impl.ts` once the real SDK has finished initializing.
 * Publishes the client, captures identity for deleteMyData, optionally calls
 * opt_in_capturing (suppressing the auto-fired `$opt_in` event), and flushes
 * the pre-init buffer.
 */
export function _setClient(
  c: PostHog,
  apiKey: string,
  distinctId: string,
  optInOnInit: boolean,
): void {
  client = c;
  _apiKey = apiKey;
  _distinctId = distinctId;
  // `captureEventName: false` suppresses PostHog's auto-fired `$opt_in`
  // event. We track the user-facing opt-in transition ourselves via the
  // `analytics_toggled` event in src/analytics/index.ts; without this flag,
  // `$opt_in` would fire on every page load (since `opt_out_capturing_by_default`
  // means we re-opt-in every boot), drowning the real signal in noise.
  if (optInOnInit) c.opt_in_capturing({ captureEventName: false });
  const queued = buffer;
  buffer = [];
  for (const { name, props } of queued) c.capture(name as string, props);
}

export function track<N extends EventName>(name: N, props: EventProps<N>): void {
  const parsed = eventSchemas[name].safeParse(props);
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      throw new Error(`analytics: invalid props for ${String(name)}: ${parsed.error.message}`);
    }
    return;
  }
  if (!client) {
    buffer.push({ name, props: parsed.data as Record<string, unknown> });
    return;
  }
  client.capture(name as string, parsed.data as Record<string, unknown>);
}

export function setOptedIn(value: boolean): void {
  if (!client) return;
  // See note on the `opt_in_capturing` call in _setClient for why we
  // suppress the auto-event.
  if (value) client.opt_in_capturing({ captureEventName: false });
  else client.opt_out_capturing();
}

export function getDistinctId(): string | undefined {
  return client?.get_distinct_id();
}

/**
 * Requests PostHog to delete all data for this person by sending a
 * `$delete_person` event to the ingest endpoint. PostHog processes
 * this as a deletion request (it queues a person-deletion job on the
 * server side). This is a best-effort call; the user should also be
 * opted out of capturing via setOptedIn(false).
 *
 * If the PostHog API ever changes this endpoint/shape, update both the
 * implementation and the comment here.
 */
export async function deleteMyData(): Promise<void> {
  if (!_apiKey || !_distinctId) return;
  await fetch(`${_apiHost}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: _apiKey,
      event: '$delete_person',
      distinct_id: _distinctId,
    }),
  });
}

/** @internal — only for tests. */
export function resetForTest(): void {
  client = null;
  buffer = [];
  _apiKey = null;
  _distinctId = null;
}
